// =============================================================================
// StageDistributionHeatmap — horizontal stacked bar pokazujacy rozklad nodow
// po stages w Content Engine. Zywy w naglowku ContentEnginePanel.
// =============================================================================
// Bucket 6 grup (nie 11 individual stages — nieczytelne w malej wizualce):
//   Czeka | Struktura w toku | Struktura ✓ | Tresc w toku | Gotowe | Bledy
//
// Hover: tooltip z top 3 najstarszymi nodami w danej grupie (stuck detect —
// >5min amber, >15min red w tooltipie).
// Klik segment: wywoluje onSegmentClick z filter value do tabeli ponizej.

import { useMemo } from "react";
import {
  Tooltip as UiTooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { AlertTriangle } from "lucide-react";

interface NodeLike {
  id: string;
  title: string;
}

interface ContentLike {
  engine_stage?: string;
  updated_at?: string;
}

interface Props {
  nodes: NodeLike[];
  contentMap: Map<string, ContentLike>;
  /** Statuses filter value (jak w ContentEnginePanel Select) */
  onSegmentClick?: (filterValue: string) => void;
  /** 'compact' = bez border/headline, inline w header; 'card' = z border/headline (legacy) */
  variant?: "compact" | "card";
}

interface StageGroup {
  key: string;
  label: string;
  stages: string[];
  color: string;
  /** Mapped to ContentEnginePanel's statusFilter values */
  filterValue: string;
}

const GROUPS: StageGroup[] = [
  { key: "pending",                label: "Czeka",              stages: ["pending"],                                                color: "#9ca3af", filterValue: "no_content" },
  { key: "structure_in_progress",  label: "Struktura w toku",   stages: ["serp_research","kg_assembly","fan_out","outline"],         color: "#06b6d4", filterValue: "in_progress" },
  { key: "outline_ready",          label: "Struktura gotowa",   stages: ["outline_ready"],                                           color: "#8b5cf6", filterValue: "outline_ready" },
  { key: "content_in_progress",    label: "Tresc w toku",       stages: ["draft","enrichment","optimization","humanization"],        color: "#f59e0b", filterValue: "in_progress" },
  { key: "done",                   label: "Gotowe",             stages: ["ready","published"],                                       color: "#10b981", filterValue: "ready" },
  { key: "failed",                 label: "Bledy",              stages: ["failed"],                                                  color: "#ef4444", filterValue: "failed" },
];

const STUCK_WARN_MS = 5 * 60 * 1000;    // >5min
const STUCK_CRITICAL_MS = 15 * 60 * 1000; // >15min

function formatAge(ms: number): string {
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s`;
  if (ms < 3600_000) return `${Math.floor(ms / 60_000)}min`;
  return `${Math.floor(ms / 3600_000)}h`;
}

export function StageDistributionHeatmap({ nodes, contentMap, onSegmentClick, variant = "compact" }: Props) {
  // Defensive guards — komponent renderuje sie na starcie ContentEnginePanel
  // zanim useThematicNodes/useThematicNodeContent zaladuja dane.
  const safeNodes = Array.isArray(nodes) ? nodes : [];
  const safeContentMap: Map<string, ContentLike> = contentMap instanceof Map ? contentMap : new Map();

  const { segments, total } = useMemo(() => {
    const buckets = new Map<string, Array<{ nodeId: string; nodeTitle: string; stage: string; ageMs: number }>>();
    for (const g of GROUPS) buckets.set(g.key, []);

    const now = Date.now();

    for (const node of safeNodes) {
      if (!node || !node.id) continue;
      const content = safeContentMap.get(node.id);
      const stage = content?.engine_stage || "pending";
      const updatedAt = content?.updated_at ? new Date(content.updated_at).getTime() : now;
      const ageMs = now - updatedAt;

      const group = GROUPS.find((g) => g.stages.includes(stage));
      if (!group) continue;

      buckets.get(group.key)!.push({ nodeId: node.id, nodeTitle: node.title ?? "", stage, ageMs });
    }

    // Sort each bucket by age DESC (stuckiest first)
    for (const list of buckets.values()) list.sort((a, b) => b.ageMs - a.ageMs);

    const segments = GROUPS.map((g) => ({
      ...g,
      items: buckets.get(g.key) ?? [],
      count: (buckets.get(g.key) ?? []).length,
    }));

    const total = safeNodes.length;
    return { segments, total };
  }, [safeNodes, safeContentMap]);

  if (total === 0) return null;

  const inProgressGroups = segments.filter((s) => s.key === "structure_in_progress" || s.key === "content_in_progress");
  const anyStuck = inProgressGroups.some((g) => g.items.some((it) => it.ageMs > STUCK_WARN_MS));

  // Compact variant: bez border/headline, mniejszy bar, legend inline; wstawiany w header
  const isCompact = variant === "compact";
  const containerCls = isCompact ? "" : "border rounded-lg p-3 bg-card";
  const barH = isCompact ? "h-3.5" : "h-6";

  return (
    <div className={containerCls}>
      {!isCompact && (
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Rozklad pipeline ({total} nodow)
          </h3>
          {anyStuck && (
            <span className="flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400">
              <AlertTriangle className="h-3 w-3" />
              stuck detect
            </span>
          )}
        </div>
      )}

      {/* Stacked horizontal bar */}
      <div className={`flex ${barH} rounded-md overflow-hidden ring-1 ring-border`}>
        <TooltipProvider delayDuration={150}>
          {segments.map((seg) => {
            const widthPct = total > 0 ? (seg.count / total) * 100 : 0;
            if (seg.count === 0) return null;
            const oldest = seg.items[0];
            const isStuck = (seg.key === "structure_in_progress" || seg.key === "content_in_progress") && oldest?.ageMs > STUCK_WARN_MS;
            const isCritical = isStuck && oldest?.ageMs > STUCK_CRITICAL_MS;
            return (
              <UiTooltip key={seg.key}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => onSegmentClick?.(seg.filterValue)}
                    className={`h-full transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-offset-background ${isCritical ? "animate-pulse" : ""}`}
                    style={{ width: `${widthPct}%`, backgroundColor: seg.color }}
                    aria-label={`${seg.label}: ${seg.count} nodow`}
                  />
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  <div className="space-y-1 min-w-[180px]">
                    <div className="flex items-center justify-between gap-4 border-b border-border pb-1 mb-1">
                      <span className="font-medium">{seg.label}</span>
                      <span className="font-mono">{seg.count} / {total} ({widthPct.toFixed(0)}%)</span>
                    </div>
                    {seg.items.slice(0, 3).map((it) => {
                      const warnCls = it.ageMs > STUCK_CRITICAL_MS ? "text-red-500" : it.ageMs > STUCK_WARN_MS ? "text-amber-500" : "text-muted-foreground";
                      return (
                        <div key={it.nodeId} className="flex items-center gap-2">
                          <span className="truncate max-w-[180px]">{it.nodeTitle || it.nodeId.slice(0, 8)}</span>
                          <span className={`ml-auto text-[10px] ${warnCls}`}>{formatAge(it.ageMs)}</span>
                        </div>
                      );
                    })}
                    {seg.items.length > 3 && (
                      <p className="text-[10px] text-muted-foreground italic">+{seg.items.length - 3} more</p>
                    )}
                    <p className="text-[10px] text-muted-foreground italic pt-1 border-t border-border mt-1">
                      Klik: filtruj tabele
                    </p>
                  </div>
                </TooltipContent>
              </UiTooltip>
            );
          })}
        </TooltipProvider>
      </div>

      {/* Legend — kompaktowa linia pod barem */}
      <div className={`flex items-center gap-2.5 flex-wrap ${isCompact ? "mt-1.5" : "mt-2"} text-[10px] text-muted-foreground`}>
        {segments.map((seg) => (
          <div key={seg.key} className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-sm" style={{ backgroundColor: seg.color }} />
            <span>{seg.label}: <span className="tabular-nums font-medium text-foreground">{seg.count}</span></span>
          </div>
        ))}
        {isCompact && anyStuck && (
          <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400 ml-auto">
            <AlertTriangle className="h-3 w-3" />
            stuck detect
          </span>
        )}
      </div>
    </div>
  );
}
